import { api } from '../client';

// ================== TYPE DEFINITIONS ==================

export enum DonationRequestStatusEnum {
  PENDING = 'Pending',
  APPROVED = 'Approved',
  REJECTED = 'Rejected',
  PROCESSED = 'Processed',
  COMPLETED = 'Completed',
  VERIFIED = 'Verified',
  AWAITING_EMPLOYER_REGISTRATION = 'Awaiting employer registration',
}

export interface Statistics {
  total_donations?: number;
  total_hours?: number;
  total_amount?: number;
  pending_requests?: number;
  approved_requests?: number;
  [key: string]: number | undefined;
}

export interface DonationStatus {
  uuid: string;
  name: DonationRequestStatusEnum;
}

export interface User {
  uuid: string;
  email: string;
  email_verified_at: string;
  full_name: string;
  phone: string;
  credit_total: number;
  user_login_method: {
    uuid: string;
    name: string;
  };
  user_role: {
    uuid: string;
    name: string;
  };
  user_status: {
    uuid: string;
    name: string;
  };
  job_title: string | null;
  location: string | null;
  department: string | null;
  total_donated_hours: number;
  total_donation_amount: number;
  total_pending_hours: number;
  last_donation_request_date: string;
}

export interface Category {
  uuid: string;
  name: string;
  description: string;
  is_active: boolean;
  sort_order: number;
}

export interface Charity {
  uuid: string;
  category_uuid: string;
  category: Category;
  name: string;
  abn: string;
  contact_email: string;
  contact_phone: string;
  website: string | null;
  head_office_location: string;
  charity_government_id: string;
  verified: number;
  verified_bsb_no: string;
  verified_acc_no: string;
  verified_acc_name: string;
}

export interface Employer {
  uuid: string;
  name: string;
  abn: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  head_office_location: string;
}

export interface DonationRequest {
  uuid: string;
  created_at: string;
  updated_at: string;
  leave_hours: number;
  leave_value: number;
  employer_top_up: number;
  total_donation: number;
  rejection_reason: string | null;
  rejected_at: string | null;
  approved_at: string | null;
  employer_comment: string | null;
  charity_received_at?: string | null;
  payment_status?: 'not_paid' | 'paid' | 'completed';
  /** Canonical lifecycle stage from the backend (App\Support\DonationLifecycle) */
  lifecycle_stage?: string;
  charity: Charity;
  employer: Employer;
  donation_request_status: DonationStatus;
  user: User;
}

export interface DonationFilters {
  employer_uuid?: string;
  user_uuid?: string;
  charity_uuid?: string;
  status?: string;
  search?: string;
  page?: number;
  per_page?: number;
  phrase?: string;
  start_date?: string;
  end_date?: string;
  donation_request_status_uuid?: string;
  lifecycle_stage?: string;
}

export interface PaginationInfo {
  current_page: number;
  last_page: number;
  per_page: number;
  current_items: number;
  from: number;
  to: number;
  total: number;
}

export interface DonationListSummary {
  total_requests: number;
  pending_requests: number;
  approved_requests: number;
  total_value: number;
}

export interface DonationResponse {
  success: boolean;
  data: DonationRequest[] | { items: DonationRequest[]; paging?: PaginationInfo; summary?: DonationListSummary };
  paging?: PaginationInfo;
  summary?: DonationListSummary;
  errors: any[];
}

// ================== DONATION SERVICE ==================

export class DonationService {
  private static readonly baseUrl = '/donation-request';

  /**
   * Get all donation requests with filters
   * 
   * @param filters Optional filters for the request
   * @returns Promise with paginated donation requests
   */
  static async getDonations(filters?: DonationFilters): Promise<DonationResponse> {
    try {
      const response = await api.get<DonationResponse>('/donation-request/admin', {
        params: filters
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch donations');
    }
  }

  /**
   * Get a donation request by UUID
   * 
   * @param uuid The UUID of the donation request
   * @returns Promise with the donation request
   */
  static async getDonationsbyUuid(uuid: string): Promise<DonationResponse> {
    try {
      const response = await api.get<DonationResponse>(`/donation-request/${uuid}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch donations');
    }
  }


  /**
   * Format currency value
   * 
   * @param value Number to format as currency
   * @returns Formatted currency string
   */
  static formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(value);
  }

  /**
   * Format date for display
   * 
   * @param dateString ISO 8601 date string
   * @returns Formatted date string
   */
  static formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(date);
    } catch (error) {
      return dateString;
    }
  }

  /**
   * Get status color for UI display
   * 
   * @param status Status name
   * @returns CSS color class or color value
   */
  static getStatusColor(status: DonationRequestStatusEnum | string): string {
    // Canonical solid tones — see TONE_PALETTE in src/lib/donationLifecycle.ts.
    switch (status.toUpperCase()) {
      case DonationRequestStatusEnum.APPROVED:
        return '#f97316'; // orange (approved)
      case DonationRequestStatusEnum.PENDING:
        return '#eab308'; // yellow (submitted)
      case DonationRequestStatusEnum.REJECTED:
        return '#ea4335'; // red
      case DonationRequestStatusEnum.PROCESSED:
        return '#22c55e'; // green (received by charity)
      default:
        return '#9ca3af'; // grey
    }
  }

  /**
   * Get status display text
   * 
   * @param status Status name
   * @returns Human-readable status text
   */
  static async deleteDonation(uuid: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete<{ success: boolean; message: string }>(`${this.baseUrl}/${uuid}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete donation');
    }
  }

  static async resendReceipt(uuid: string): Promise<{ success: boolean; message?: string; error?: any }> {
    try {
      const response = await api.post<{ success: boolean; message?: string }>(`${this.baseUrl}/${uuid}/resend-receipt`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error,
        message: error.response?.data?.message || 'Failed to resend invoice email'
      };
    }
  }

  static getStatusText(status: DonationRequestStatusEnum | string): string {
    switch (status.toUpperCase()) {
      case DonationRequestStatusEnum.APPROVED:
        return 'Approved';
      case DonationRequestStatusEnum.PENDING:
        return 'Pending';
      case DonationRequestStatusEnum.REJECTED:
        return 'Rejected';
      case DonationRequestStatusEnum.PROCESSED:
        return 'Processed';
      default:
        return 'Unknown';
    }
  }
}

// Export singleton instance for backward compatibility
export const donationService = DonationService; 